import { Injectable } from '@angular/core';
import { createSelector, select, Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';

import { ExpositionEndpointMetadataEntity } from '../components/exposition-endpoint-metadata-config/exposition-endpoint-metadata.entity';
import { ExpositionHistoryListEntity } from '../components/exposition-history-list/exposition-history-list.entity';
import { ExpositionMetadataEntity } from '../components/exposition-internal-metadata-config/exposition-metadata.entity';
import { ExpositionItemEntity } from '../components/exposition-list/exposition-item.entity';
import { ExpositionListEntity } from '../components/exposition-list/exposition-list.entity';
import { ExpositionDatablockListEntity } from '../components/exposition-stepper/exposition-datablock-list/exposition-datablock-list.entity';
import { ExpositionAccessListEntity } from '../components/exposition-view/exposition-access-list/exposition-access-list.entity';
import { ExpositionEntity } from '../components/exposition-view/exposition.entity';
import { PublishedExpositionEntity } from '../components/exposition-view/published-exposition/published-exposition.entity';
import { AccessConfigEntity } from '../domain/exposition-access-config.entity';
import { ExpositionColumnConfigListEntity } from '../domain/exposition-column-config-list.entity';
import { ExpositionConsumerMappingEntity } from '../domain/exposition-consumer-mapping.entity';
import {
	ExpositionOriginalSliceState,
	ExpositionSliceKey,
} from './expositions.state';
import { ExpositionRootState, featureSelector } from './feature.store';

const baseSelector = createSelector(
	featureSelector,
	(state) => state[ExpositionSliceKey]
);

const consumerToMap = createSelector(baseSelector, (state) =>
	state.get('consumersToMap')
);

@Injectable()
export class ExpositionsSelector {
	public constructor(private readonly store: Store<ExpositionRootState>) {}

	private get state(): Observable<ExpositionOriginalSliceState> {
		return this.store.pipe(select(baseSelector));
	}

	public getIsLoading$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('loading')));
	}

	public getActionInProgress$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('actionInProgress')));
	}

	public isSavingInProgress$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('isSavingExposition')));
	}

	public getIsLoadingDatablockColumns$(): Observable<boolean> {
		return this.state.pipe(
			map((state) => state.get('isLoadingDatablockColumns'))
		);
	}

	public getColumnConfigList$(): Observable<ExpositionColumnConfigListEntity> {
		return this.state.pipe(map((state) => state.get('stepper').columns));
	}

	public getDatablockList$(): Observable<ExpositionDatablockListEntity> {
		return this.state.pipe(map((state) => state.get('stepper').datablocks));
	}

	public getDatablockLabel$(id: number): Observable<string> {
		return this.state.pipe(
			map(
				(state) => state.stepper.datablocks.elements.get(`${id}`)?.label ?? ''
			)
		);
	}

	public getExpositionView$(): Observable<ExpositionEntity> {
		return this.state.pipe(map((state) => state.get('inViewExposition')));
	}

	public getExpositionId$(): Observable<number> {
		return this.state.pipe(map((state) => state.get('inViewExposition').id));
	}

	public getColumns$(): Observable<ExpositionColumnConfigListEntity> {
		return this.state.pipe(
			map((state) => state.get('inViewExposition').columns)
		);
	}
	public getAccessList$(): Observable<ExpositionAccessListEntity> {
		return this.state.pipe(
			map((state) => state.get('inViewExposition').accessPoints)
		);
	}
	public getMetadata$(): Observable<ExpositionMetadataEntity> {
		return this.state.pipe(
			map((state) => state.get('inViewExposition').metadata)
		);
	}
	public getEndpointMetadata$(): Observable<ExpositionEndpointMetadataEntity> {
		return this.state.pipe(
			map((state) => state.get('inViewExposition').endpointMetadata)
		);
	}
	public getPublishedExposition$(): Observable<PublishedExpositionEntity> {
		return this.state.pipe(
			map((state) => state.get('inViewExposition').published)
		);
	}
	public getAccessConfig$(): Observable<AccessConfigEntity> {
		return this.state.pipe(
			map((state) => state.get('inViewExposition').accessPoints.currentConfig)
		);
	}

	public getExpositions$(): Observable<ExpositionListEntity> {
		return this.state.pipe(map((state) => state.get('expositions')));
	}

	public canCreateNewExposition$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('permissions').create));
	}

	public getCanDeleteExposition$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('permissions').delete));
	}

	public getCanEditExposition$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('permissions').edit));
	}

	public getUnmappedConsumers$(): Observable<
		Array<ExpositionConsumerMappingEntity>
	> {
		return this.store.pipe(select(consumerToMap));
	}

	public getSelectedExpositions$(
		selected: Array<string>
	): Observable<Array<ExpositionItemEntity>> {
		return this.getExpositions$().pipe(
			map((list) =>
				selected.reduce<Array<ExpositionItemEntity>>((acc, curr) => {
					const maybe = list.elements.get(`${curr}`);
					if (maybe) {
						acc.push(maybe);
						return acc;
					}
					return acc;
				}, [])
			)
		);
	}

	public getHistory$(): Observable<ExpositionHistoryListEntity> {
		return this.state.pipe(map((state) => state.get('history')));
	}

	public getIsLoadingJobErrorLogs$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('isLoadingJobErrorLogs')));
	}
	public getIsLoadingMetadataInfo$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('isLoadingMetadataInfo')));
	}

	public hasConfiguredOpenAccess$(accessId: string): Observable<boolean> {
		return this.getExpositionView$().pipe(
			map((initExpositionView) =>
				initExpositionView.accessPoints.hasConfiguredOpenAccess(accessId)
			)
		);
	}
}
